import React, { useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import { ModalToaster } from '../Toaster/Toaster';
import DownloadButton from '../../Printing/Buttons/Download';
import OptionsButton from '../../Printing/Buttons/Options';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

export const DialogTitle = (props) => {
  const { children, downloadRecord, onClose, onOptions, options, showOptions, optionsChange,
          titleActions, icon, statusChip, sx, ...other } = props;
  const theme = useTheme();
  const headerBg = alpha(theme.palette.primary.main, 0.08);
  return (
    <MuiDialogTitle
      {...other}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        bgcolor: headerBg,
        borderBottom: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        {icon ? (
          <Box sx={{ color: theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        ) : null}
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontFamily: 'Poppins, Futura PT, Prompt',
            fontSize: '1.1rem',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {children}
        </Typography>
        {statusChip}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {downloadRecord != null ? (
          <DownloadButton type='icon' record={downloadRecord} />
        ) : null}
        {onOptions != null ? (
          <OptionsButton
            onChange={optionsChange}
            options={options}
            toggleDialog={onOptions}
            open={showOptions}
          />
        ) : null}
        {titleActions}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    </MuiDialogTitle>
  );
};

export const DialogButtons = (props) => {
  const { buttonStack } = props;
  const theme = useTheme();
  return (
    <DialogActions
      sx={{
        px: 3,
        py: 2,
        gap: 1,
        bgcolor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`
      }}
    >
      {buttonStack}
    </DialogActions>
  );
};

export default function ModalDialog(props) {
  const { buttonStack, children, title, titleActions, icon, statusChip, toggleDialog, dialogStatus,
          fullWidth, disableClickAway, maxWidth = 'sm', onScroll, scrollExists, fScreen = 'sm',
          notification, downloadRecord, onOptions, options, showOptions, optionsChange, paperSx,
          fullScreen: forceFullScreen, TransitionComponent, dialogSx } = props;
  const theme = useTheme();
  const breakpointFullScreen = useMediaQuery(th => th.breakpoints.down(fScreen));
  const fullScreen = forceFullScreen != null ? forceFullScreen : breakpointFullScreen;
  const contentElementRef = useRef(null);
  useEffect(() => {
    if (dialogStatus) {
      const descriptionElement = contentElementRef.current;
      if (descriptionElement !== null && scrollExists != null) {
        scrollExists(descriptionElement.scrollHeight > descriptionElement.clientHeight);
      }
    }
  }, [dialogStatus, scrollExists]);

  return (
    <Dialog
      open={dialogStatus}
      onClose={(event, reason) => {
        if (disableClickAway && reason === 'backdropClick') {
          return;
        }
        toggleDialog();
      }}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={fullScreen ? undefined : PaperComponent}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
          ...paperSx
        }
      }}
      TransitionComponent={TransitionComponent}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      scroll='paper'
      sx={dialogSx}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ cursor: 'move' }}
        onClose={toggleDialog}
        titleActions={titleActions}
        icon={icon}
        statusChip={statusChip}
        downloadRecord={downloadRecord}
        onOptions={onOptions}
        options={options}
        showOptions={showOptions}
        optionsChange={optionsChange}
      >
        {title}
      </DialogTitle>
      <DialogContent
        onScroll={onScroll}
        ref={contentElementRef}
        sx={{
          p: 3,
          bgcolor: theme.palette.background.paper,
          '&&': { paddingTop: theme.spacing(3) }
        }}
      >
        {children}
        <ModalToaster alert={notification} />
      </DialogContent>
      {buttonStack != null &&
        <DialogButtons buttonStack={buttonStack} />
      }
    </Dialog>
  );
}
